/*
 * @providesModule GameOperationsTutorialController
 */

import {BUTTONS_DIVIDER_CONTAINER_WIDTH} from '@skillz/ui';

import GameOperationsTrainerStepWelcome from './trainers/GameOperationsTrainerStepWelcome';
import GameOperationsTrainerBase from './trainers/GameOperationsTrainerBase';
import {useMemo, useState} from 'react';
import {GameOperationsTutorialStep} from './trainers/GameOperationsTrainerBase.config';
import GameOperationsTrainerStepBanner from './trainers/GameOperationsTrainerStepBanner';
import GameOperationsTrainerStepButtons from './trainers/GameOperationsTrainerStepButtons';
import {FTUE_INTERSTITIALS_VIEW} from 'AWSConstants';
import AWSManager from 'AWSManager';
import ModalNavigationController from 'ModalNavigationController';
import GameOperationsTutorial from 'GameOperationsTutorial';
import {Dimensions} from 'react-native';
import Sizes from 'Sizes';
import {TABLET_PADDING_HORIZONTAL} from 'MatchSelectListStyle';
import Preferences from 'Preferences';
import ClientPropertyConstants from 'ClientPropertyConstants';
import SKZConstants from 'SKZConstants';
import {FTUE_INTERSTITIAL_KEY} from 'UserPropertiesConstants';
import {updateUserProperties} from 'UserPropertiesController';

//#region Configs

/** The tutorial steps in order */
export const ORDERED_CONVERSIONS_STEPS: GameOperationsTutorialStep[] = [
  'welcome',
  'highlightBanners',
  'highlightButtons',
  'completed',
];

type TrainerBuilder = {
  build: () => GameOperationsTrainerBase | null;
};

/**
 * The TrainerBuilderByStepMap is a mapping of tutorial steps (GameOperationsTutorialStep) to their corresponding TrainerBuilder implementations.
 * Each step in the tutorial has a corresponding builder that constructs the appropriate trainer instance.
 */
const TrainerBuilderByStepMap: Record<GameOperationsTutorialStep, TrainerBuilder> = {
  welcome: {build: () => new GameOperationsTrainerStepWelcome()},
  highlightBanners: {build: () => new GameOperationsTrainerStepBanner()},
  highlightButtons: {build: () => new GameOperationsTrainerStepButtons()},
  completed: {build: () => null},
};

//#endregion

/**
 * Manages logic for the Game Operation Container conversions flow, in which the user will be
 * notified of the changes on Home Screen top UI
 */
export const useGameOperationsTutorialTrainer = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(
    () => +Preferences.get(SKZConstants.SKZ_GAME_OPERATIONS_TUTORIAL_STEP_INDEX) || 0
  );

  const currentStep = ORDERED_CONVERSIONS_STEPS[currentStepIndex];
  const trainerProps = TrainerBuilderByStepMap[currentStep].build();

  // tutorial is completed whenever the user reaches the last step or the tutorial is disabled
  const isCompleted =
    currentStepIndex === ORDERED_CONVERSIONS_STEPS.length - 1 ||
    !ClientPropertyConstants.getGameOperationsTutorialEnabled();

  const advanceStep = () => {
    setCurrentStepIndex((prev) => {
      const nextStepIndex = prev + 1;

      Preferences.set({
        [SKZConstants.SKZ_GAME_OPERATIONS_TUTORIAL_STEP_INDEX]: nextStepIndex,
      });

      if (nextStepIndex === ORDERED_CONVERSIONS_STEPS.length - 1) {
        updateUserProperties(FTUE_INTERSTITIAL_KEY, 'true');
        AWSManager.trackEvent(FTUE_INTERSTITIALS_VIEW);
      }

      return nextStepIndex;
    });
  };

  return {
    currentStep,
    trainerProps,
    isCompleted,
    advanceStep,
  };
};

/**
 * Get the calculated X offset for the arrow in the tutorial for each step.
 * The arrow is placed at the middle of each game operation container
 */
export const useArrowOffsetsByStepMap = (): Record<
  GameOperationsTutorialStep,
  {x: number; y: number}
> => {
  return useMemo(() => {
    const screenWith = Dimensions.get('window').width;

    // if its a tablet, we need to consider the additional padding to calculate the middle of the containers
    const tabletHorizontalPadding = Sizes.isTablet()
      ? TABLET_PADDING_HORIZONTAL?.paddingHorizontal
      : 0;

    const buttonsContainerWidth = BUTTONS_DIVIDER_CONTAINER_WIDTH;
    const bannersContainerWidth = screenWith - buttonsContainerWidth - tabletHorizontalPadding * 2;

    // arrow is placed at the middle of the overlay components
    // which occupy the whole screen width
    const arrowCurrentPosition = screenWith / 2;

    // calculate the middle of each container
    const middleBannersX = tabletHorizontalPadding + bannersContainerWidth / 2;
    const middleButtonsX =
      tabletHorizontalPadding + bannersContainerWidth + buttonsContainerWidth / 2;

    // calculate the offset of the arrow from the middle of the overlay component
    const middleBannersXOffset = middleBannersX - arrowCurrentPosition;
    const middleButtonsXOffset = middleButtonsX - arrowCurrentPosition;

    return {
      welcome: {x: 0, y: 0},
      highlightBanners: {x: middleBannersXOffset, y: 0},
      highlightButtons: {x: middleButtonsXOffset, y: 0},
      completed: {x: 0, y: 0},
    };
  }, []);
};

export const showGameOperationsTutorialModal = ({onComplete}: {onComplete: () => void}) => {
  const onCompleteTutorial = () => {
    ModalNavigationController.hideModal();
    onComplete();
  };

  ModalNavigationController.hideModal(() => {
    ModalNavigationController.showModal(
      <GameOperationsTutorial onCompleted={onCompleteTutorial} />,
      {
        animationType: 'fade',
        modalBackHandler: () => {
          onComplete();
          ModalNavigationController.hideModal();
        },
      }
    );
  });
};
